/**
 * 对话服务（docs/语音助手.md「对话服务」，与 iOS VoiceAssistant/ChatProvider.swift 一致）：
 * 阿里云百炼 / DeepSeek / 家长配置的 OpenAI 兼容服务。都由浏览器直接调用，不经过我们的服务端。
 */

export type ProviderKind = "dashscope" | "deepseek" | "custom";

export interface Option {
  id: string;
  name: string;
}

/** 百炼的对话模型（孩子平板可选的） */
export const QWEN_MODELS: Option[] = [
  { id: "qwen-plus", name: "通义千问 Plus（推荐）" },
  { id: "qwen-max", name: "通义千问 Max（更聪明，稍贵）" },
  { id: "qwen-flash", name: "通义千问 Flash（最快最便宜）" },
];

/** 家长端可选的模型：另外可以选能看图的 VL 模型（发图片时自动切换到它） */
export const PARENT_QWEN_MODELS: Option[] = [...QWEN_MODELS, { id: "qwen3-vl-plus", name: "通义千问 VL Plus（能看图片）" }];

export const DEEPSEEK_MODELS: Option[] = [
  { id: "deepseek-flash", name: "DeepSeek Flash（推荐，快）" },
  { id: "deepseek-v4-pro", name: "DeepSeek V4 Pro（更聪明，稍慢）" },
];

/** 发图片时改用的看图模型 */
export const VISION_MODEL = "qwen3-vl-plus";

/** 思考等级：qwen-plus / qwen-flash / qwen3-vl-plus 支持，qwen-max 不支持 */
export const THINKING_LEVELS: Option[] = [
  { id: "off", name: "关闭（最快）" },
  { id: "low", name: "低" },
  { id: "medium", name: "中" },
  { id: "high", name: "高（最慢）" },
];

/** 孩子平板上的朗读音色（CosyVoice 系统音色，与 iOS / 安卓一致；网页版不朗读，只用于设置平板） */
export const VOICES: Option[] = [
  { id: "longhuhu_v3", name: "龙呼呼 · 天真女童" },
  { id: "longpaopao_v3", name: "龙泡泡 · 泡泡音童声" },
  { id: "longjielidou_v3", name: "龙杰力豆 · 顽皮男孩" },
  { id: "longniuniu_v3", name: "龙牛牛 · 阳光男孩" },
  { id: "longxian_v3", name: "龙仙 · 可爱女孩" },
  { id: "longling_v3", name: "龙铃 · 稚气女孩" },
  { id: "longshanshan_v3", name: "龙闪闪 · 戏剧童声" },
  { id: "longhouge_v3", name: "龙猴哥 · 经典猴哥" },
  { id: "longanwen_v3", name: "龙安温 · 知性女声" },
  { id: "longanrou_v3", name: "龙安柔 · 温柔女声" },
  { id: "longxing_v3", name: "龙星 · 邻家姐姐" },
  { id: "longanqin_v3", name: "龙安亲 · 活泼女声" },
  { id: "longwan_v3", name: "龙婉 · 细腻柔声" },
  { id: "longyuan_v3", name: "龙媛 · 温暖治愈" },
  { id: "longanyang", name: "龙安洋 · 阳光男生" },
  { id: "longze_v3", name: "龙泽 · 元气男声" },
  { id: "longanyun_v3", name: "龙安昀 · 居家暖男" },
  { id: "longcheng_v3", name: "龙橙 · 智慧青年" },
  { id: "longmiao_v3", name: "龙妙 · 讲故事姐姐" },
  { id: "longyue_v3", name: "龙悦 · 磁性女声" },
  { id: "longsanshu_v3", name: "龙三叔 · 讲故事叔叔" },
  { id: "longlaobo_v3", name: "龙老伯 · 爷爷" },
  { id: "longlaoyi_v3", name: "龙老姨 · 奶奶" },
  { id: "longjiaxin_v3", name: "龙嘉欣 · 粤语" },
  { id: "longlaotie_v3", name: "龙老铁 · 东北话" },
];

/** 学生端默认音色 */
export const DEFAULT_STUDENT_VOICE = "longhuhu_v3";

/** 实际用的音色：不在列表里（空的、或换 CosyVoice 之前的 Cherry 等旧音色）时用默认 */
export function resolveVoice(id: string | null | undefined): string {
  return id && VOICES.some((v) => v.id === id) ? id : DEFAULT_STUDENT_VOICE;
}

/** 孩子每天最多提问次数（0 = 不限），默认是契约里的 50 */
export const DAILY_LIMITS: [number, string][] = [
  [20, "20 次"],
  [50, "50 次"],
  [100, "100 次"],
  [0, "不限"],
];

export interface ChatProvider {
  kind: ProviderKind;
  /** 显示名称 */
  name: string;
  /** chat completions 地址 */
  chatUrl: string;
  /** 可选模型（第一个是默认） */
  models: Option[];
}

export const DASHSCOPE: ChatProvider = {
  kind: "dashscope",
  name: "阿里云百炼",
  chatUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1/chat/completions",
  models: PARENT_QWEN_MODELS,
};

export const DEEPSEEK: ChatProvider = {
  kind: "deepseek",
  name: "DeepSeek",
  chatUrl: "https://api.deepseek.com/chat/completions",
  models: DEEPSEEK_MODELS,
};

/** 自定义服务的地址：没写到 /chat/completions 时自动补上；必须是 https */
export function customChatUrl(baseUrl: string): string | null {
  let s = baseUrl.trim();
  while (s.endsWith("/")) s = s.slice(0, -1);
  if (!s.endsWith("/chat/completions")) s += "/chat/completions";
  try {
    const u = new URL(s);
    if (u.protocol !== "https:" || !u.host) return null;
    return u.toString();
  } catch {
    return null;
  }
}

/** 家长配置的 OpenAI 兼容服务；地址不对或没有模型时返回 null */
export function customProvider(name: string, baseUrl: string, models: string[]): ChatProvider | null {
  const url = customChatUrl(baseUrl);
  if (!url || !models.length) return null;
  return { kind: "custom", name, chatUrl: url, models: models.map((m) => ({ id: m, name: m })) };
}

/** 模型属于这个服务时原样返回，否则用默认模型 */
export function resolveModel(p: ChatProvider, model: string | null | undefined): string {
  if (model && p.models.some((m) => m.id === model)) return model;
  return p.models[0]?.id ?? "";
}

/** 能否开启思考：百炼除 qwen-max 外都可以，DeepSeek 都可以；自定义服务不发思考参数（返回了思考内容照样显示） */
export function supportsThinking(p: ChatProvider, model: string): boolean {
  switch (p.kind) {
    case "dashscope":
      return model !== "qwen-max";
    case "deepseek":
      return true;
    default:
      return false;
  }
}

/** 拿到工具结果后再请求时是否继续思考：通义千问会把同样的思考从头再来一遍，所以只在第一步思考；DeepSeek 接着上次的思考往下想 */
export function thinksEveryStep(p: ChatProvider): boolean {
  return p.kind === "deepseek";
}

/** 开启思考时要把之前助手消息的 reasoning_content 一起发回去：DeepSeek 带工具时必须，否则返回 400 */
export function sendsReasoningBack(p: ChatProvider): boolean {
  return p.kind === "deepseek";
}

/** 思考等级 → 百炼的 thinking_budget；关闭时为 null */
export function thinkingBudget(level: string): number | null {
  switch (level) {
    case "low":
      return 1024;
    case "medium":
      return 4096;
    case "high":
      return 16384;
    default:
      return null;
  }
}

/** 思考等级 → DeepSeek 的 reasoning_effort */
export function deepseekEffort(level: string): string {
  switch (level) {
    case "low":
      return "low";
    case "high":
      return "max";
    default:
      return "high";
  }
}

/** 往请求体里写思考参数；level 为 off / low / medium / high，null 表示这一步不思考 */
export function applyThinking(body: Record<string, unknown>, p: ChatProvider, model: string, level: string | null): void {
  const on = level != null && level !== "off";
  switch (p.kind) {
    case "dashscope": {
      if (!supportsThinking(p, model)) return;
      const budget = on ? thinkingBudget(level!) : null;
      body.enable_thinking = budget != null;
      if (budget != null) body.thinking_budget = budget;
      return;
    }
    case "deepseek":
      // DeepSeek 默认开启思考，关闭要明确写
      body.thinking = { type: on ? "enabled" : "disabled" };
      if (on) body.reasoning_effort = deepseekEffort(level!);
      return;
    default:
      return;
  }
}

/** 发给模型的一条消息（OpenAI 格式） */
export type ChatMessage = Record<string, unknown> & { role: string };

/** 工具定义（function calling） */
export type ToolSpec = { type: "function"; function: { name: string; description: string; parameters: unknown } };

/** 一次流式请求的请求体 */
export function buildChatBody(opts: { provider: ChatProvider; model: string; messages: ChatMessage[]; tools: ToolSpec[]; thinking: string | null }): Record<string, unknown> {
  const body: Record<string, unknown> = { model: opts.model, messages: opts.messages, stream: true, temperature: 0.3 };
  if (opts.tools.length) {
    body.tools = opts.tools;
    // 百炼要明确打开并行调用；其它服务用默认值（有的不认这个参数）
    if (opts.provider.kind === "dashscope") body.parallel_tool_calls = true;
  }
  applyThinking(body, opts.provider, opts.model, opts.thinking);
  return body;
}

/** 接口错误转成中文提示 */
export function providerError(p: ChatProvider, status: number, bodyText: string): string {
  let msg = bodyText;
  try {
    const obj = JSON.parse(bodyText) as { error?: { message?: string } | string; message?: string };
    const inner = typeof obj.error === "object" && obj.error ? obj.error : obj;
    if (typeof (inner as { message?: string }).message === "string") msg = (inner as { message: string }).message;
  } catch {
    // 不是 JSON
  }
  const short = msg.slice(0, 120);
  if (p.kind === "dashscope") {
    if (status === 401) return "百炼 API Key 无效，请在「助手设置」里检查";
    if (status === 429) return "请求太频繁或额度不足，请稍后再试";
    return `百炼接口出错（${status}）：${short}`;
  }
  const label = p.kind === "deepseek" ? "DeepSeek" : `自定义服务（${p.name}）`;
  if (status === 401) return `${label}的 API Key 无效，请在「助手设置」里检查`;
  if (status === 402) return `${label}的余额不足`;
  if (status === 429) return `${label}请求太频繁或额度不足，请稍后再试`;
  return `${label}接口出错（${status}）：${short}`;
}

/**
 * 有图片时这一轮用的服务和模型：选的不是百炼的 VL 模型时改用百炼的 qwen3-vl-plus
 * （DeepSeek 和自定义服务不能看图）。返回 switched 表示要改用百炼的 Key。
 */
export function visionRoute(p: ChatProvider, model: string, hasImages: boolean): { provider: ChatProvider; model: string; switched: boolean } {
  if (!hasImages || (p.kind === "dashscope" && model.includes("vl"))) return { provider: p, model, switched: false };
  return { provider: DASHSCOPE, model: VISION_MODEL, switched: true };
}
